import React, { useState, useEffect, useRef } from "react";
import { Spin } from "antd";
import { useLocation } from "react-router-dom";
import { ChevronRight, ChevronLeft } from "lucide-react";
import {
  FileTextOutlined,
  MoneyCollectOutlined,
} from "@ant-design/icons";
import { useDasPolicyDetails } from "../../hooks/products/das/useDasPolicyDetails";
import { useDasPolicyStatementSummary } from "../../hooks/products/das/useDasPolicyStatementSummary";
import { useDasContributionsStatement } from "../../hooks/products/das/useDasContributionsStatement";
import { useDasContributionYears } from "../../hooks/products/das/useDasContributionYears";
import { notifyUser } from "../../util/util";
import { getDasMemberCertificate } from "../../services/das.service";

function formatCurrency(value) {
  if (value === null || value === undefined || value === "") return "N/A";
  const num = Number(value);
  if (isNaN(num)) return String(value);
  return `GHS ${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(dateStr) {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function DasProductDetails({ productDetails }) {
  const location = useLocation();
  const stateProductDetails = location.state?.productDetails || productDetails;

  // pd = normalized predictable shape used by the UI
  const pd = React.useMemo(() => {
    const src = stateProductDetails || {};
    return {
      policyName: src.policyName || src.schemeName || src.name || "DAS Policy",
      policyNumber: src.policyNumber || src.policy_no || "",
      status: src.status || "",
      memberName: src.memberName || src.full_name || src.member_name || "",
      memberNumber: src.memberNumber || src.member_no || "",
      memberId: src.memberId || src.member_id || "",
      email: src.email || "",
      mobileNo: src.mobileNo || src.mobile_no || "",
      schemeId: src.schemeId || src.scheme_id || "",
      schemeName: src.schemeName || src.scheme_name || "",
      policyDescription: src.policyDescription || src.policy_description || src.product_description || "",
      grossPremium: src.grossPremium ?? src.gross_premium ?? null,
      fixedSum: src.fixedSum ?? src.fixed_sum ?? null,
      quotedSum: src.quotedSum ?? src.quoted_sum ?? null,
      quotedPremium: src.quotedPremium ?? src.quoted_premium ?? null,
      renewalDate: src.renewalDate || src.renewal_date || "",
      startDate: src.startDate || src.start_date || "",
    };
  }, [stateProductDetails]);

  const [detailsEnabled, setDetailsEnabled] = useState(false);
  const { data: detailsData, isLoading: detailsLoading } = useDasPolicyDetails(
    pd.policyNumber,
    detailsEnabled
  );

  // Fetch full policy details once a policy number is available
  useEffect(() => {
    if (pd.policyNumber) {
      setDetailsEnabled(true);
    }
  }, [pd.policyNumber]);

  const policy = detailsData?.data?.policy || null;

  // Statement generation
  const [statementEnabled, setStatementEnabled] = useState(false);
  const { data: statementData, refetch: refetchStatement, isFetching: statementFetching } =
    useDasPolicyStatementSummary(
      { policyNumber: pd.policyNumber, statement_type: 'summary', format: 'pdf' },
      statementEnabled
    );

  useEffect(() => {
    if (statementEnabled && statementData) {
      const downloadUrl =
        statementData?.data?.download_url ||
        statementData?.data?.url ||
        statementData?.download_url ||
        statementData?.url;
      if (downloadUrl) {
        window.open(downloadUrl, "_blank");
      } else if (statementData.success === false) {
        notifyUser("error", statementData.message || "Failed to generate statement. Please try again.", "Error");
      } else {
        notifyUser("error", "Statement generation failed. Please try again.", "Error");
      }
      setStatementEnabled(false);
    }
  }, [statementEnabled, statementData]);

  const handleGenerateStatement = () => {
    if (!pd.policyNumber) {
      notifyUser("error", "Policy number is missing.", "Error");
      return;
    }
    setStatementEnabled(true);
    setTimeout(() => refetchStatement(), 0);
  };

  const [certificateFetching, setCertificateFetching] = useState(false);

  const handleDownloadMemberCertificate = async () => {
    if (!pd.policyNumber) {
      notifyUser("error", "Policy number is missing.", "Error");
      return;
    }

    setCertificateFetching(true);
    try {
      const response = await getDasMemberCertificate(pd.policyNumber);
      const contentType = response.headers?.["content-type"] || "";

      if (contentType.includes("application/json")) {
        const payload = JSON.parse(await response.data.text());
        const certificateUrl = payload?.data?.download_url || payload?.data?.url || payload?.data;

        if (!payload?.success || typeof certificateUrl !== "string") {
          throw new Error(payload?.message || payload?.error || "Certificate download failed.");
        }

        window.open(certificateUrl, "_blank", "noopener,noreferrer");
        return;
      }

      const downloadUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = "DAS-member-certificate-" +
        pd.policyNumber.replace(/[^A-Za-z0-9_-]/g, "_") +
        ".pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("DAS member certificate download error:", error);
      notifyUser(
        "error",
        error.message || "Failed to download member certificate. Please try again.",
        "Download Error"
      );
    } finally {
      setCertificateFetching(false);
    }
  };

  // --- Contributions statement download: year-selection modal -> statement API ---
  const [statementModalOpen, setStatementModalOpen] = useState(false);
  const [statementStartYear, setStatementStartYear] = useState(null);
  const [statementEndYear, setStatementEndYear] = useState(null);
  const [statementError, setStatementError] = useState("");

  const { mutate: downloadContributionsStatement, isPending: isDownloadingStatement } =
    useDasContributionsStatement();

  // Fetch available contribution years from /contributed/years (same endpoint as pensions)
  const [yearsEnabled, setYearsEnabled] = useState(false);
  const { data: contributionYearsData } = useDasContributionYears(yearsEnabled);

  const currentYear = new Date().getFullYear();

  const yearOptions = React.useMemo(() => {
    const list = Array.isArray(contributionYearsData?.data)
      ? contributionYearsData.data
      : [];
    const apiYears = list
      .map((item) => Number(item.fund_year ?? item.fundYear))
      .filter(Boolean);
    if (apiYears.length === 0) {
      return Array.from({ length: 6 }, (_, i) => currentYear - i);
    }
    return [...new Set(apiYears)].sort((a, b) => b - a);
  }, [contributionYearsData, currentYear]);

  // When the start year changes, clear an end year that is not in the future of it
  useEffect(() => {
    if (statementStartYear && statementEndYear && statementEndYear < statementStartYear) {
      setStatementEndYear(null);
    }
  }, [statementStartYear, statementEndYear]);

  const openStatementModal = () => {
    setStatementError("");
    setStatementStartYear(null);
    setStatementEndYear(null);
    setYearsEnabled(true);
    setStatementModalOpen(true);
  };

  const closeStatementModal = () => setStatementModalOpen(false);

  const handleContributionsStatementDownload = () => {
    if (!pd.policyNumber) {
      notifyUser("error", "Policy number is missing.", "Error");
      return;
    }
    if (!statementStartYear || !statementEndYear) {
      setStatementError("Please select both a start and an end year.");
      return;
    }
    if (statementEndYear < statementStartYear) {
      setStatementError("End year cannot be before the start year.");
      return;
    }
    setStatementError("");
    setStatementModalOpen(false);
    downloadContributionsStatement(
      { policyNumber: pd.policyNumber, startYear: statementStartYear, endYear: statementEndYear },
      {
        onSuccess: (res) => {
          const url = res?.data?.data || res?.data;
          if (url) {
            window.open(url, "_blank");
            notifyUser("success", "Contributions statement is being downloaded.", "Success");
          } else {
            notifyUser("error", "No statement URL returned. Please try again.", "Error");
          }
        },
        onError: (err) => {
          notifyUser(
            "error",
            err?.response?.data?.message ||
              err?.message ||
              "Failed to download contributions statement. Please try again.",
            "Download Error"
          );
        },
      }
    );
  };

  // Horizontal scroll for action buttons
  const buttonContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const container = buttonContainerRef.current;
    if (!container) return;
    const update = () => {
      setCanScrollLeft(container.scrollLeft > 8);
      setCanScrollRight(container.scrollLeft + container.clientWidth < container.scrollWidth - 8);
    };
    update();
    container.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      container.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  if (!stateProductDetails) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <div style={{ color: "#888", fontSize: 18 }}>
          No product selected. Please go back and select a DAS policy.
        </div>
      </div>
    );
  }

  const detailRows = [
    { label: "Policy Number", value: pd.policyNumber || policy?.policy_no || "N/A", strong: true },
    { label: "Member Name", value: pd.memberName || policy?.full_name || "N/A" },
    { label: "Member Number", value: pd.memberNumber || policy?.member_no || "N/A" },
    { label: "Member ID", value: pd.memberId || policy?.member_id || "N/A" },
    { label: "Mobile Number", value: pd.mobileNo || policy?.telephone || "N/A" },
    { label: "Email", value: pd.email || policy?.Email || "N/A" },
    { label: "Scheme Name", value: pd.schemeName || policy?.scheme_name || "N/A" },
    { label: "Scheme ID", value: pd.schemeId || policy?.schemeID || policy?.scheme_id || "N/A" },
    { label: "Policy Description", value: pd.policyDescription || policy?.product_description || "N/A" },
    { label: "Gross Premium", value: formatCurrency(policy?.gross_premium ?? policy?.grossPremium ?? pd.grossPremium) },
    { label: "Fixed Sum", value: formatCurrency(policy?.fixed_sum ?? policy?.fixedSum ?? pd.fixedSum) },
    { label: "Quoted Sum", value: formatCurrency(policy?.quoted_sum ?? policy?.quotedSum ?? pd.quotedSum) },
    { label: "Quoted Premium", value: formatCurrency(policy?.quoted_premium ?? policy?.quotedPremium ?? pd.quotedPremium) },
    { label: "Renewal Date", value: formatDate(policy?.renewal_date || policy?.renewalDate || pd.renewalDate) },
    { label: "Start Date", value: formatDate(policy?.start_date || policy?.startDate || policy?.commence_date || policy?.date_from || pd.startDate) },
  ];

  return (
    <div
      className="product-details-wrapper"
      style={{
        width: "100%",
        maxWidth: 700,
        display: "flex",
        flexDirection: "column",
        gap: 24,
        alignItems: "center",
        margin: "0 auto",
        padding: isMobile ? 8 : 24,
      }}
    >
      <div
        className="product-details-card"
        style={{
          borderRadius: 18,
          boxShadow: "0 4px 24px 0 rgba(0,0,0,0.10)",
          padding: "32px 48px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 0,
          maxWidth: 700,
          overflow: "hidden",
          background: "#fff",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 24, width: "100%" }}>
          <span style={{ fontWeight: 800, fontSize: 20, color: "#000000ff" }}>
            {pd.policyName}
          </span>

          {/* Action Buttons */}
          <div style={{ position: "relative", width: "100%", overflow: "hidden" }}>
            <div
              className="buttons-scroll-wrapper button-container"
              ref={buttonContainerRef}
              style={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                alignItems: "center",
                gap: 10,
                width: "100%",
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              <button
                type="button"
                className="tab-scroll-btn"
                aria-label="Scroll buttons left"
                onClick={() => buttonContainerRef.current && buttonContainerRef.current.scrollBy({ left: -120, behavior: "smooth" })}
                disabled={!canScrollLeft}
                style={{ opacity: canScrollLeft ? 1 : 0, position: "relative", flexShrink: 0, width: 36, height: 36, borderRadius: "50%", border: "none", display: "flex", alignItems: "center", justifyContent: "center", background: "#00b050", boxShadow: "0 10px 25px rgba(0, 176, 80, 0.35)", cursor: "pointer", transition: "opacity 0.2s, transform 0.2s, boxShadow 0.2s", zIndex: 30, color: "#fff", marginRight: 8 }}
              >
                <ChevronLeft size={18} />
              </button>

              <div
                className="button-card"
                style={{
                  display: "none",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 4,
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: 8,
                  cursor: "pointer",
                  minWidth: 100,
                  flexShrink: 0,
                }}
                role="button"
                tabIndex={0}
                aria-label="Generate Statement"
                onClick={handleGenerateStatement}
                onKeyDown={e => { if (e.key === "Enter" || e.key === " ") handleGenerateStatement(); }}
              >
                {statementFetching ? <Spin size="small" /> : <FileTextOutlined style={{ fontSize: 24, color: "#00704A" }} />}
                <span
                  style={{
                    fontSize: 12,
                    textAlign: "center",
                    fontWeight: 600,
                    color: "#00704A",
                  }}
                >
                  Statement
                </span>
              </div>

              <div
                className="button-card"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 4,
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: 8,
                  cursor: certificateFetching ? "wait" : "pointer",
                  minWidth: 100,
                  flexShrink: 0,
                  opacity: certificateFetching ? 0.7 : 1,
                }}
                role="button"
                tabIndex={0}
                aria-label="Download Member Certificate"
                onClick={certificateFetching ? undefined : handleDownloadMemberCertificate}
                onKeyDown={e => {
                  if (!certificateFetching && (e.key === "Enter" || e.key === " ")) {
                    handleDownloadMemberCertificate();
                  }
                }}
              >
                {certificateFetching ? (
                  <Spin size="small" />
                ) : (
                  <FileTextOutlined style={{ fontSize: 24, color: "#00704A" }} />
                )}
                <span
                  style={{
                    fontSize: 12,
                    textAlign: "center",
                    fontWeight: 600,
                    color: "#00704A",
                  }}
                >
                  Certificate
                </span>
              </div>

              <div
                className="button-card"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 4,
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: 8,
                  cursor: "pointer",
                  minWidth: 100,
                  flexShrink: 0,
                }}
                role="button"
                tabIndex={0}
                aria-label="View Contributions"
                onClick={openStatementModal}
                onKeyDown={e => { if (e.key === "Enter" || e.key === " ") openStatementModal(); }}
              >
                {isDownloadingStatement ? <Spin size="small" /> : <MoneyCollectOutlined style={{ fontSize: 24, color: "#00704A" }} />}
                <span
                  style={{
                    fontSize: 12,
                    textAlign: "center",
                    fontWeight: 600,
                    color: "#00704A",
                  }}
                >
                  Member statement
                </span>
              </div>

              <button
                type="button"
                className="tab-scroll-btn"
                aria-label="Scroll buttons right"
                onClick={() => buttonContainerRef.current && buttonContainerRef.current.scrollBy({ left: 120, behavior: "smooth" })}
                disabled={!canScrollRight}
                style={{ opacity: canScrollRight ? 1 : 0, position: "relative", flexShrink: 0, width: 36, height: 36, borderRadius: "50%", border: "none", display: "flex", alignItems: "center", justifyContent: "center", background: "#00b050", boxShadow: "0 10px 25px rgba(0, 176, 80, 0.35)", cursor: "pointer", transition: "opacity 0.2s, transform 0.2s, boxShadow 0.2s", zIndex: 30, color: "#fff", marginLeft: 8 }}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>

        <div style={{ height: 18 }} />

        {/* Loading full details */}
        {detailsLoading && (
          <div style={{ textAlign: "center", padding: "40px 20px" }}>
            <Spin size="large" tip="Loading policy details..." />
          </div>
        )}

        {/* Details List */}
        {!detailsLoading && (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {detailRows.map((row, idx) => (
              <div key={idx} style={{ borderTop: "1px solid #eee", padding: "10px 0", textAlign: "left" }}>
                <div style={{ color: "#444", fontWeight: row.strong ? 800 : 700, fontSize: 18 }}>
                  {row.value}
                </div>
                <div style={{ color: "#888", fontWeight: 500, fontSize: 13, marginTop: 2 }}>
                  {row.label}
                </div>
              </div>
            ))}
          </div>
        )}

        <style>{`
          .button-card {
            transition: transform 0.3s ease;
          }
          .button-card:hover {
            transform: scale(1.05);
          }
          .buttons-scroll-wrapper::-webkit-scrollbar { display: none; }
          .button-container { -ms-overflow-style: none; scrollbar-width: none; }
          .tab-scroll-btn:disabled { pointer-events: none; opacity: 0; }
          .tab-scroll-btn:hover:not(:disabled) {
            transform: translateY(-50%) scale(1.08);
            box-shadow: 0 12px 28px rgba(0, 176, 80, 0.45);
          }
          @media (max-width: 600px) {
            .product-details-card { padding: 20px 12px !important; }
            .button-card {
              min-width: 56px !important;
              max-width: 56px !important;
              padding: 3px 2px !important;
              gap: 3px !important;
            }
            .button-card img {
              width: 14px !important;
              height: 14px !important;
            }
            .button-card .anticon {
              font-size: 14px !important;
            }
            .button-card span {
              font-size: 7px !important;
              line-height: 1.1 !important;
            }
            .tab-scroll-btn { display: none !important; }
          }
        `}</style>
      </div>

      {/* Contributions Statement Download Modal */}
      {statementModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.18)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={closeStatementModal}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 18,
              minWidth: 320,
              maxWidth: 460,
              width: "100%",
              boxShadow: "0 8px 40px 0 rgba(0,0,0,0.13)",
              padding: 0,
              position: "relative",
              display: "flex",
              flexDirection: "column",
              maxHeight: "75vh",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: "24px 32px 0 32px" }}>
              <div
                style={{
                  fontWeight: 800,
                  fontSize: 20,
                  color: "#222",
                  marginBottom: 6,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>Download Contributions Statement</span>
                <button
                  onClick={closeStatementModal}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: 22,
                    color: "#888",
                    cursor: "pointer",
                    marginLeft: 12,
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <p style={{ color: "#888", fontSize: 15, marginBottom: 18, marginTop: 0 }}>
                Select the start and end years for the contributions statement you want to download.
              </p>
            </div>

            <div style={{ padding: "0 32px 24px 32px", overflow: "visible", flex: 1, minHeight: 0 }}>
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <omk-select
                    label="Start Year"
                    style={{ width: "100%" }}
                    value={statementStartYear || ""}
                    onInput={(e) => { setStatementStartYear(e.target.value ? Number(e.target.value) : null); setStatementError(""); }}
                  >
                    {yearOptions.map(y => <omk-select-option key={`s-${y}`} value={y}>{y}</omk-select-option>)}
                  </omk-select>
                  <omk-select
                    label="End Year"
                    style={{ width: "100%" }}
                    value={statementEndYear || ""}
                    onInput={(e) => { setStatementEndYear(e.target.value ? Number(e.target.value) : null); setStatementError(""); }}
                  >
                    {yearOptions
                      .filter(y => !statementStartYear || y >= statementStartYear)
                      .map(y => <omk-select-option key={`e-${y}`} value={y}>{y}</omk-select-option>)}
                  </omk-select>
                </div>
                {statementError && <div style={{ color: "#c0392b", fontSize: 13, marginTop: 8 }}>{statementError}</div>}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 20 }}>
                  <button type="button" className="btn btn-white" onClick={closeStatementModal} disabled={isDownloadingStatement}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleContributionsStatementDownload}
                    disabled={isDownloadingStatement}
                  >
                    {isDownloadingStatement ? (
                      <>
                        <Spin size="small" /> Downloading...
                      </>
                    ) : (
                      <>Download</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DasProductDetails;
